import { useEffect, useState } from 'react';
import { adminApi } from '@formosa/shared/api/admin';
import {
  defaultUserStatsDateRange,
  formatReportCount,
  validateUserStatsDateRange,
} from './userStatisticsReportConstants';
import {
  buildImCsReportParams,
  formatDurationMs,
  formatRatio,
} from './imCsReportConstants';

const DEFAULT_RANGE = defaultUserStatsDateRange();

const EMPTY_FILTERS = {
  startDateInput: DEFAULT_RANGE.startDateInput,
  endDateInput: DEFAULT_RANGE.endDateInput,
  agentAdminId: '',
};

const EMPTY_SUMMARY = {
  inboundCount: 0,
  claimedCount: 0,
  endedCount: 0,
  handleDurationSampleCount: 0,
  avgHandleDurationMs: null,
  p50HandleDurationMs: null,
  p95HandleDurationMs: null,
  slaHandleSeconds: 60,
  slaMetCount: 0,
  slaAttainmentRate: null,
  ratedCount: 0,
  goodCount: 0,
  goodRate: null,
};

export default function ImCsReportPage() {
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [summary, setSummary] = useState(EMPTY_SUMMARY);
  const [agentRows, setAgentRows] = useState([]);
  const [meta, setMeta] = useState({ moduleEnabled: true, channelModeSkipped: false });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  async function load(nextFilters = filters) {
    const rangeError = validateUserStatsDateRange(
      nextFilters.startDateInput,
      nextFilters.endDateInput
    );
    if (rangeError) {
      setError(rangeError);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const data = await adminApi.imCsReportInterval(buildImCsReportParams(nextFilters));
      setMeta({
        moduleEnabled: data?.moduleEnabled !== false,
        channelModeSkipped: !!data?.channelModeSkipped,
      });
      setSummary(data?.sessionSummary || EMPTY_SUMMARY);
      setAgentRows(data?.agentRows || []);
    } catch (err) {
      setError(err.message || '載入失敗');
      setSummary(EMPTY_SUMMARY);
      setAgentRows([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function onFilterChange(key, value) {
    setFilters((prev) => ({ ...prev, [key]: value }));
  }

  return (
    <div className="page-panel">
      <div className="page-header">
        <h2>客服報表</h2>
        <p className="page-desc">處理時長、轉接標註、SLA 達標率與好評率（最後一評）。</p>
      </div>

      <div className="toolbar filters-row">
        <label>
          開始日期
          <input
            type="date"
            value={filters.startDateInput}
            onChange={(e) => onFilterChange('startDateInput', e.target.value)}
          />
        </label>
        <label>
          結束日期
          <input
            type="date"
            value={filters.endDateInput}
            onChange={(e) => onFilterChange('endDateInput', e.target.value)}
          />
        </label>
        <label>
          客服 Admin ID（可選）
          <input
            type="text"
            value={filters.agentAdminId}
            placeholder="篩選 Agent 服務段"
            onChange={(e) => onFilterChange('agentAdminId', e.target.value)}
          />
        </label>
        <button type="button" className="btn-primary" disabled={loading} onClick={() => load()}>
          {loading ? '查詢中…' : '查詢'}
        </button>
      </div>

      {error ? <div className="form-error">{error}</div> : null}
      {!meta.moduleEnabled ? (
        <div className="form-error">
          客服報表模組已關閉（formosa.report.modules.enabled.im-cs=false；舊鍵 im 仍相容）
        </div>
      ) : null}
      {meta.channelModeSkipped ? (
        <div className="form-error">目前通道非 FORMOSA_IM，無自建客服 Session 資料</div>
      ) : null}

      <div className="stats-summary-cards" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(140px,1fr))', gap: 12, marginBottom: 16 }}>
        <SummaryCard label="進線" value={formatReportCount(summary.inboundCount)} />
        <SummaryCard label="首次接取" value={formatReportCount(summary.claimedCount)} />
        <SummaryCard label="結束" value={formatReportCount(summary.endedCount)} />
        <SummaryCard label="時長樣本" value={formatReportCount(summary.handleDurationSampleCount)} />
        <SummaryCard label="平均時長" value={formatDurationMs(summary.avgHandleDurationMs)} />
        <SummaryCard label="P50" value={formatDurationMs(summary.p50HandleDurationMs)} />
        <SummaryCard label="P95" value={formatDurationMs(summary.p95HandleDurationMs)} />
        <SummaryCard
          label={`SLA 達標（≤${summary.slaHandleSeconds ?? 60}s）`}
          value={formatRatio(summary.slaAttainmentRate)}
        />
        <SummaryCard label="SLA 達標數" value={formatReportCount(summary.slaMetCount)} />
        <SummaryCard label="已評分" value={formatReportCount(summary.ratedCount)} />
        <SummaryCard label="好評數" value={formatReportCount(summary.goodCount)} />
        <SummaryCard label="好評率" value={formatRatio(summary.goodRate)} />
      </div>

      <div className="table-wrap">
        <table className="data-table">
          <thead>
            <tr>
              <th>客服 ID</th>
              <th>帳號</th>
              <th>服務段</th>
              <th>一般段</th>
              <th>轉接段</th>
              <th>轉接占比</th>
              <th>總時長</th>
              <th>一般時長</th>
              <th>轉接時長</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={9}>載入中…</td>
              </tr>
            ) : agentRows.length === 0 ? (
              <tr>
                <td colSpan={9}>無資料</td>
              </tr>
            ) : (
              agentRows.map((row) => (
                <tr key={row.agentAdminId}>
                  <td>{row.agentAdminId}</td>
                  <td>{row.agentDisplayName || row.agentUsername || '—'}</td>
                  <td>{formatReportCount(row.segmentCount)}</td>
                  <td>{formatReportCount(row.normalSegmentCount)}</td>
                  <td>{formatReportCount(row.transferSegmentCount)}</td>
                  <td>{formatRatio(row.transferSegmentRatio)}</td>
                  <td>{formatDurationMs(row.totalHandleDurationMs)}</td>
                  <td>{formatDurationMs(row.normalHandleDurationMs)}</td>
                  <td>{formatDurationMs(row.transferHandleDurationMs)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SummaryCard({ label, value }) {
  return (
    <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: '10px 12px' }}>
      <div style={{ fontSize: 12, color: '#6b7280' }}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: 600, marginTop: 4 }}>{value}</div>
    </div>
  );
}
